import Editor from "@monaco-editor/react";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { Box, Chip, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import moment from "moment";
import React, { useEffect, useState } from "react";
import SystemLogService from "./SystemLogService";

const levelConfig = {
    INFO: { color: "info", icon: InfoIcon, label: "Info" },
    WARN: { color: "warning", icon: WarningIcon, label: "Warn" },
    ERROR: { color: "error", icon: ErrorIcon, label: "Error" },
    SYSTEM: { color: "default", icon: InfoIcon, label: "System" }
};

export default function LogDetailDialog({ open, onClose, log }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && log?.id) {
            setLoading(true);
            SystemLogService.findById(log.id).then((result) => {
                setLoading(false);
                if (result.success && result.data) {
                    setDetail(result.data);
                } else {
                    setDetail(log);
                }
            });
        } else {
            setDetail(log);
        }
    }, [open, log?.id]);

    if (!detail && !log) return null;

    const data = detail || log;
    const config = levelConfig[data.level] || levelConfig.INFO;
    const Icon = config.icon;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>Log Detail</span>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Typography color="text.secondary">Loading...</Typography>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                                size="small"
                                icon={<Icon sx={{ fontSize: 16 }} />}
                                label={config.label}
                                color={config.color}
                                variant="outlined"
                            />
                            {data.source && (
                                <Typography variant="body2" color="text.secondary">
                                    Source: {data.source}
                                </Typography>
                            )}
                        </Box>
                        {data.createdAt && (
                            <Typography variant="body2" color="text.secondary">
                                {moment(data.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                            </Typography>
                        )}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Message
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    p: 1,
                                    bgcolor: "action.hover",
                                    borderRadius: 1
                                }}
                            >
                                {data.message ?? ""}
                            </Typography>
                        </Box>
                        {data.stackTrace && (
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Stack Trace
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        p: 1,
                                        bgcolor: "action.hover",
                                        borderRadius: 1,
                                        fontSize: "0.75rem",
                                        overflow: "auto",
                                        maxHeight: 200
                                    }}
                                >
                                    {data.stackTrace}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Details
                            </Typography>
                            {data.details != null && typeof data.details === "object" && Object.keys(data.details).length > 0 ? (
                                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", minHeight: 120, maxHeight: 320 }}>
                                    <Editor
                                        height="240px"
                                        language="json"
                                        value={JSON.stringify(data.details, null, 2)}
                                        options={{
                                            readOnly: true,
                                            minimap: { enabled: false },
                                            scrollBeyondLastLine: false,
                                            fontSize: 12
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1, fontStyle: "italic" }}>
                                    No data
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
